import React, { useMemo, useState } from 'react';
import { Button, Modal, Select, Table, message } from 'antd';
import type { TableColumnsType } from 'antd';
import { useMutation, useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { Navigate } from 'react-router-dom';
import { useWarehouseContext } from '../../../../contexts/WarehouseContext';
import { SiteType } from 'graphql/types/SiteType';
import TablePaginationBar from '../../../../components/TablePaginationBar';
import { hasResource } from 'utils/resourceAuth';
import { ResourceCode } from 'constants/resourceCodes';
import { showError } from 'utils/exceptionUtils';
import BatchSetLaborTeamModal from './components/BatchSetLaborTeamModal';
import {
    BATCH_UPDATE_LIMIT,
    GET_SORTING_LABOR_TEAM_LISTING,
    LABOR_TEAM_UNASSIGNED_FILTER,
    SortingLaborTeamAccount,
    SortingLaborTeamFiltersInput,
    SortingLaborTeamListingResponse,
} from './graphql/queries/getSortingLaborTeamListing';
import {
    LIST_LABOR_TEAM_NAMES,
    LIST_RECENT_LABOR_TEAM_NAMES,
    ListLaborTeamNamesResponse,
    ListRecentLaborTeamNamesResponse,
    RECENT_LABOR_TEAM_TAG_LIMIT,
} from './graphql/queries/listLaborTeamNames';
import {
    BATCH_UPDATE_SORTING_LABOR_TEAM,
    UPDATE_SORTING_LABOR_TEAM,
} from './graphql/mutations/updateSortingLaborTeam';
import InputFilterItem from '../../../delivery/components/InputFilterItem';
import { getSmartLabelClassName } from '../../../../utils/labelWidthUtils';

interface FilterState {
    name: string;
    phone: string;
    laborTeamName: string;
}

const DEFAULT_FILTERS: FilterState = {
    name: '',
    phone: '',
    laborTeamName: '',
};

const SortingLaborTeamPage: React.FC = () => {
    const { t } = useTranslation();
    const { currentWarehouse } = useWarehouseContext();
    const canEdit = hasResource([ResourceCode.menu.HUB_ACCOUNT_LABOR_TEAM_EDIT]);
    const hubId = currentWarehouse?.id ? Number(currentWarehouse.id) : undefined;
    const isHub = currentWarehouse?.siteType === SiteType.HUB;

    const [filters, setFilters] = useState<FilterState>(DEFAULT_FILTERS);
    const [appliedFilters, setAppliedFilters] = useState<FilterState>(DEFAULT_FILTERS);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
    const [modalOpen, setModalOpen] = useState(false);
    const [editingAccounts, setEditingAccounts] = useState<SortingLaborTeamAccount[]>([]);

    const buildFiltersInput = (state: FilterState): SortingLaborTeamFiltersInput | undefined => {
        const input: SortingLaborTeamFiltersInput = {};
        if (state.name.trim()) {
            input.name = state.name.trim();
        }
        if (state.phone.trim()) {
            input.phone = state.phone.trim();
        }
        if (state.laborTeamName) {
            input.laborTeamName = state.laborTeamName;
        }
        return Object.keys(input).length > 0 ? input : undefined;
    };

    const { data, loading, refetch } = useQuery<SortingLaborTeamListingResponse>(GET_SORTING_LABOR_TEAM_LISTING, {
        variables: {
            hubId,
            filters: buildFiltersInput(appliedFilters),
            pageRequest: { page: currentPage, size: pageSize, sortBy: 'id', sortDirection: 'DESC' },
        },
        skip: !hubId || !isHub,
        fetchPolicy: 'network-only',
    });

    const { data: laborTeamNamesData, refetch: refetchLaborTeamNames } = useQuery<ListLaborTeamNamesResponse>(
        LIST_LABOR_TEAM_NAMES,
        {
            variables: { hubId },
            skip: !hubId || !isHub,
            fetchPolicy: 'network-only',
        }
    );

    const { data: recentLaborTeamNamesData, refetch: refetchRecentLaborTeamNames } =
        useQuery<ListRecentLaborTeamNamesResponse>(LIST_RECENT_LABOR_TEAM_NAMES, {
            variables: { hubId, limit: RECENT_LABOR_TEAM_TAG_LIMIT },
            skip: !hubId || !isHub,
            fetchPolicy: 'network-only',
        });

    const [updateSortingLaborTeam, { loading: updating }] = useMutation(UPDATE_SORTING_LABOR_TEAM);
    const [batchUpdateSortingLaborTeam, { loading: batchUpdating }] = useMutation(BATCH_UPDATE_SORTING_LABOR_TEAM);

    const listing = data?.getSortingLaborTeamListing;
    const tableData = listing?.content ?? [];
    const laborTeamOptions = laborTeamNamesData?.listLaborTeamNames ?? [];
    const recentLaborTeamOptions = recentLaborTeamNamesData?.listRecentLaborTeamNames ?? [];

    const laborTeamFilterOptions = useMemo(() => {
        const options = [
            { value: '', label: t('wms.sortingLaborTeam.filters.all') },
            { value: LABOR_TEAM_UNASSIGNED_FILTER, label: t('wms.sortingLaborTeam.unassigned') },
            ...laborTeamOptions.map((name) => ({ value: name, label: name })),
        ];
        return options;
    }, [laborTeamOptions, t]);

    if (!isHub) {
        return <Navigate to="/wms/account" replace />;
    }

    const handleFilter = () => {
        setAppliedFilters(filters);
        setCurrentPage(1);
        setSelectedRowKeys([]);
    };

    const handleReset = () => {
        setFilters(DEFAULT_FILTERS);
        setAppliedFilters(DEFAULT_FILTERS);
        setCurrentPage(1);
        setSelectedRowKeys([]);
    };

    const refreshAll = async () => {
        await refetch();
        await refetchLaborTeamNames();
        await refetchRecentLaborTeamNames();
    };

    const openBatchModal = (accounts: SortingLaborTeamAccount[]) => {
        setEditingAccounts(accounts);
        setModalOpen(true);
    };

    const handleEdit = (record: SortingLaborTeamAccount) => {
        openBatchModal([record]);
    };

    const handleBatchSet = () => {
        if (selectedRowKeys.length === 0) {
            message.warning(t('wms.sortingLaborTeam.messages.selectAtLeastOne'));
            return;
        }
        if (selectedRowKeys.length > BATCH_UPDATE_LIMIT) {
            Modal.warning({
                content: t('wms.sortingLaborTeam.messages.batchLimitExceeded', { limit: BATCH_UPDATE_LIMIT }),
            });
            return;
        }
        const selected = tableData.filter((item) => selectedRowKeys.includes(item.accountId));
        if (selected.length === 0) {
            message.warning(t('wms.sortingLaborTeam.messages.selectAtLeastOne'));
            return;
        }
        openBatchModal(selected);
    };

    const handleSaveLaborTeam = async (laborTeamName: string) => {
        if (!hubId) {
            return;
        }
        try {
            if (editingAccounts.length === 1) {
                const result = await updateSortingLaborTeam({
                    variables: {
                        input: {
                            hubId,
                            accountId: Number(editingAccounts[0].accountId),
                            laborTeamName,
                        },
                    },
                });
                if (!result.data?.updateSortingLaborTeam) {
                    message.error(t('wms.sortingLaborTeam.messages.updateFailed'));
                    return;
                }
                message.success(t('wms.sortingLaborTeam.messages.updateSuccess'));
            } else {
                const result = await batchUpdateSortingLaborTeam({
                    variables: {
                        input: {
                            hubId,
                            accountIds: editingAccounts.map((item) => Number(item.accountId)),
                            laborTeamName,
                        },
                    },
                });
                const batchResult = result.data?.batchUpdateSortingLaborTeam;
                if (!batchResult || batchResult.successCount === 0) {
                    message.error(t('wms.sortingLaborTeam.messages.updateFailed'));
                    return;
                }
                if (batchResult.failedAccountIds.length > 0) {
                    message.warning(
                        t('wms.sortingLaborTeam.messages.partialSuccess', {
                            success: batchResult.successCount,
                            failed: batchResult.failedAccountIds.length,
                        })
                    );
                } else {
                    message.success(t('wms.sortingLaborTeam.messages.updateSuccess'));
                }
            }
            setModalOpen(false);
            setEditingAccounts([]);
            setSelectedRowKeys([]);
            await refreshAll();
        } catch (error) {
            showError(error);
        }
    };

    const columns: TableColumnsType<SortingLaborTeamAccount> = [
        {
            title: t('wms.sortingLaborTeam.columns.name'),
            dataIndex: 'name',
            key: 'name',
        },
        {
            title: t('wms.sortingLaborTeam.columns.phone'),
            dataIndex: 'phone',
            key: 'phone',
            render: (phone: string | null | undefined) => phone || '-',
        },
        {
            title: t('wms.sortingLaborTeam.columns.laborTeam'),
            dataIndex: 'laborTeamName',
            key: 'laborTeamName',
            render: (value: string | null | undefined) => (
                <span className={value ? '' : 'text-gray-400'}>
                    {value || t('wms.sortingLaborTeam.unassigned')}
                </span>
            ),
        },
        {
            title: t('wms.sortingLaborTeam.columns.actions'),
            key: 'actions',
            render: (_: unknown, record: SortingLaborTeamAccount) => (
                canEdit ? (
                    <Button type="link" onClick={() => handleEdit(record)} style={{ paddingLeft: 0 }}>
                        {t('wms.sortingLaborTeam.buttons.edit')}
                    </Button>
                ) : (
                    '-'
                )
            ),
        },
    ];

    const filterLabels = [
        t('wms.sortingLaborTeam.filters.name'),
        t('wms.sortingLaborTeam.filters.phone'),
        t('wms.sortingLaborTeam.filters.laborTeam'),
    ];

    return (
        <div className="bg-white rounded-lg shadow-sm px-3 pb-3 pt-0">
            <div className="mb-2 pl-0 py-2">
                <div className="grid grid-cols-3 gap-x-10 gap-y-4 items-start">
                    <InputFilterItem
                        label={t('wms.sortingLaborTeam.filters.name')}
                        value={filters.name}
                        onChange={(e) => setFilters({ ...filters, name: e.target.value })}
                        spanClassName={getSmartLabelClassName(filterLabels)}
                    />
                    <InputFilterItem
                        label={t('wms.sortingLaborTeam.filters.phone')}
                        value={filters.phone}
                        onChange={(e) => setFilters({ ...filters, phone: e.target.value })}
                        spanClassName={getSmartLabelClassName(filterLabels)}
                    />
                    <div className="min-w-0 flex items-center gap-2">
                        <span className={getSmartLabelClassName(filterLabels)}>{t('wms.sortingLaborTeam.filters.laborTeam')}</span>
                        <Select
                            className="flex-1"
                            value={filters.laborTeamName}
                            options={laborTeamFilterOptions}
                            onChange={(value) => setFilters({ ...filters, laborTeamName: value })}
                            placeholder={t('wms.sortingLaborTeam.filters.all') as string}
                        />
                    </div>
                </div>
            </div>

            {canEdit && (
                <div className="flex items-center gap-3 mb-3">
                    <span>{t('wms.sortingLaborTeam.selectedCount', { count: selectedRowKeys.length })}</span>
                    <Button
                        type="primary"
                        onClick={handleBatchSet}
                        disabled={selectedRowKeys.length === 0}
                    >
                        {t('wms.sortingLaborTeam.buttons.batchSet')}
                    </Button>
                    <Button
                        onClick={() => setSelectedRowKeys([])}
                        disabled={selectedRowKeys.length === 0}
                    >
                        {t('wms.sortingLaborTeam.buttons.cancelSelection')}
                    </Button>
                </div>
            )}

            <div className="flex justify-end items-center mb-3 gap-2">
                <Button type="primary" onClick={handleFilter}>
                    {t('wms.account.filters.filter')}
                </Button>
                <Button onClick={handleReset}>
                    {t('wms.account.filters.reset')}
                </Button>
            </div>

            <Table
                rowKey="accountId"
                loading={loading}
                columns={columns}
                dataSource={tableData}
                pagination={false}
                rowSelection={canEdit ? {
                    selectedRowKeys,
                    onChange: (keys) => {
                        setSelectedRowKeys(keys);
                    },
                } : undefined}
                scroll={{ x: 'max-content' }}
                size="middle"
            />

            <TablePaginationBar
                total={listing?.totalElements ?? 0}
                current={currentPage}
                pageSize={pageSize}
                onChange={(page, size) => {
                    setCurrentPage(page);
                    setPageSize(size);
                    setSelectedRowKeys([]);
                }}
                onShowSizeChange={(_, size) => {
                    setCurrentPage(1);
                    setPageSize(size);
                    setSelectedRowKeys([]);
                }}
                pageSizeOptions={['10', '20', '50']}
            />

            <BatchSetLaborTeamModal
                open={modalOpen}
                accounts={editingAccounts.map((item) => ({
                    accountId: item.accountId,
                    name: item.name,
                }))}
                recentLaborTeamOptions={recentLaborTeamOptions}
                loading={updating || batchUpdating}
                onCancel={() => {
                    setModalOpen(false);
                    setEditingAccounts([]);
                }}
                onSave={handleSaveLaborTeam}
            />
        </div>
    );
};

export default SortingLaborTeamPage;
